import { useCallback, useEffect, useReducer } from "react";
import { useTranslation } from "react-i18next";
import { Building2, ChevronRight, Loader2, Phone, Search } from "lucide-react";
import {
  getInsuranceByWildcard,
  InsuranceAvatar,
} from "@ascleo/insurances-client";
import { docCirrusRpcClient } from "../../../../../lib/rpc/doc-cirrus.client";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";
import { KioskTextField } from "../../../../../components/kiosk-field";

/**
 * A statutory insurer from the insurer catalogue search.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const insurer: InsurerSearchResult = { iknr: "109519005", name: "AOK Bayern" };
 * ```
 */
export interface InsurerSearchResult {
  readonly iknr: string;
  readonly name: string;
  readonly shortName?: string;
  readonly phone?: string;
}

interface InsurerSearchState {
  readonly query: string;
  readonly results: readonly InsurerSearchResult[];
  readonly isSearching: boolean;
  readonly hasSearched: boolean;
  readonly failed: boolean;
}

type InsurerSearchAction =
  | { type: "set-query"; query: string }
  | { type: "reset-short-query" }
  | { type: "search-start" }
  | { type: "search-success"; results: readonly InsurerSearchResult[] }
  | { type: "search-error" };

const INITIAL_STATE: InsurerSearchState = {
  query: "",
  results: [],
  isSearching: false,
  hasSearched: false,
  failed: false,
};

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

function insurerSearchReducer(
  state: InsurerSearchState,
  action: InsurerSearchAction,
): InsurerSearchState {
  switch (action.type) {
    case "set-query":
      return { ...state, query: action.query };
    case "reset-short-query":
      return { ...state, results: [], isSearching: false, hasSearched: false, failed: false };
    case "search-start":
      return { ...state, isSearching: true, failed: false };
    case "search-success":
      return {
        ...state,
        results: action.results,
        isSearching: false,
        hasSearched: true,
        failed: false,
      };
    case "search-error":
      return { ...state, results: [], isSearching: false, failed: true };
  }
}

/**
 * Debounced statutory-insurer search shared by registration and the coverage
 * flow, so both show the same results UI (KC-81).
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * const search = useInsurerSearch();
 * search.setQuery("AOK");
 * ```
 */
export function useInsurerSearch() {
  const [state, dispatch] = useReducer(insurerSearchReducer, INITIAL_STATE);

  const runSearch = useCallback(async (query: string) => {
    if (query.length < MIN_QUERY_LENGTH) {
      dispatch({ type: "reset-short-query" });
      return;
    }
    dispatch({ type: "search-start" });
    try {
      const results = await docCirrusRpcClient.frontdesk.searchInsurers({ query });
      dispatch({ type: "search-success", results: [...results] });
    } catch (error) {
      frontdeskDiagnosticLogger.error("Insurer search failed", {
        error,
        queryLength: query.length,
      });
      dispatch({ type: "search-error" });
    }
  }, []);

  useEffect(
    function debounceInsurerSearch() {
      const timer = setTimeout(() => {
        void runSearch(state.query);
      }, SEARCH_DEBOUNCE_MS);
      return function cancelPendingInsurerSearch() {
        clearTimeout(timer);
      };
    },
    [runSearch, state.query],
  );

  const setQuery = useCallback((query: string) => {
    dispatch({ type: "set-query", query });
  }, []);

  return {
    ...state,
    setQuery,
    /** True while fewer than two characters were typed: nothing to search yet. */
    isShortQuery: state.query.length < MIN_QUERY_LENGTH,
  };
}

/**
 * The insurer search input: 56px kiosk field with a search icon, a spinner
 * while searching and the "at least 2 characters" hint as plain helper text
 * instead of an empty box (KC-48).
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <InsurerSearchField id="insurer-search" testId="insurer-search-input" search={search} />
 * ```
 */
export function InsurerSearchField({
  id,
  testId,
  search,
}: {
  readonly id: string;
  readonly testId: string;
  readonly search: ReturnType<typeof useInsurerSearch>;
}) {
  const { t } = useTranslation();
  return (
    <KioskTextField
      id={id}
      testId={testId}
      type="search"
      label={t("self-service.check-in.insurer-search.label")}
      icon={Search}
      autoComplete="off"
      value={search.query}
      onChange={search.setQuery}
      description={
        search.isShortQuery ? t("self-service.check-in.insurer-search.hint") : undefined
      }
      endAddon={
        search.isSearching ? (
          <Loader2 aria-hidden className="size-6 animate-spin text-muted-foreground" />
        ) : undefined
      }
    />
  );
}

/**
 * One result list for every insurer search: bordered 64px+ rows with the
 * insurer logo (or a building icon), the name, the short name only when it
 * differs, the phone number when known, and a chevron (KC-81).
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <InsurerResultList results={search.results} testIdPrefix="insurer-result" onSelect={select} />
 * ```
 */
export function InsurerResultList({
  results,
  testIdPrefix,
  onSelect,
}: {
  readonly results: readonly InsurerSearchResult[];
  /** Each row gets `${testIdPrefix}-${iknr}`. */
  readonly testIdPrefix: string;
  readonly onSelect: (insurer: InsurerSearchResult) => void;
}) {
  return (
    <ul className="m-0 flex list-none flex-col gap-3 p-0">
      {results.map((insurer) => {
        const identity = getInsuranceByWildcard(insurer.name)[0] ?? null;
        const shortName =
          insurer.shortName !== undefined &&
          insurer.shortName.trim() !== "" &&
          insurer.shortName.trim() !== insurer.name.trim()
            ? insurer.shortName
            : undefined;
        return (
          <li key={insurer.iknr}>
            <button
              type="button"
              data-testid={`${testIdPrefix}-${insurer.iknr}`}
              onClick={() => onSelect(insurer)}
              className="flex min-h-16 w-full min-w-0 items-center gap-4 rounded-2xl border bg-card px-5 py-4 text-left transition-colors hover:border-primary-vivid hover:bg-primary-soft focus-visible:ring-4 focus-visible:ring-ring/45 focus-visible:outline-none"
            >
              {identity ? (
                <InsuranceAvatar
                  identity={identity}
                  size="md"
                  showBackground={true}
                  className="shrink-0 p-1"
                />
              ) : (
                <span
                  aria-hidden
                  className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary-subtle text-primary-ink [&_svg]:size-6"
                >
                  <Building2 />
                </span>
              )}
              <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="text-lg leading-snug font-semibold break-words">
                  {insurer.name}
                </span>
                {shortName ? (
                  <span className="text-base text-foreground">{shortName}</span>
                ) : null}
                {insurer.phone ? (
                  <span className="flex items-center gap-2 text-base text-foreground">
                    <Phone aria-hidden className="size-4 shrink-0" />
                    {insurer.phone}
                  </span>
                ) : null}
              </span>
              <ChevronRight aria-hidden className="size-6 shrink-0 text-muted-foreground" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
